import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { Progress } from "./components/ui/progress";
import RichiestaColloquioModal from "./components/RichiestaColloquioModal";
import ScopriDiPiuModal from "./components/ScopriDiPiuModal";
import AreaProfiloCandidato from "./components/AreaProfiloCandidato";
import ImpersonationBanner from "./components/ImpersonationBanner";
import MatchCandidato from "./components/MatchCandidato";
import AgenzieCompatibili from "./components/AgenzieCompatibili";
import WhatsAppButton from "./components/WhatsAppButton";
import FormazioneView from "./components/FormazioneView";
import ColloquiList from "./components/ColloquiList";
import CVPreviewModal from "./components/CVPreviewModal";
import { 
  Building2, 
  MapPin, 
  Phone, 
  User, 
  Star,
  TrendingUp,
  Eye,
  Calendar,
  Briefcase,
  Target,
  Users,
  Home,
  MessageSquare,
  Settings,
  UserCircle,
  ArrowRight,
  Heart,
  Share,
  MessageCircle,
  BookOpen,
  Camera,
  Bell,
  HelpCircle,
  LogOut,
  FileText,
  X,
  Upload,
  Send
} from "lucide-react";
import { agentAdvancerMain } from './images';
import FeedCandidati from "./components/FeedCandidati";
import FeedAgenzie from "./components/FeedAgenzie";
import FeedbackRiepilogoCandidato from "./components/FeedbackRiepilogoCandidato";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const CandidatoDashboard = ({ candidatoId }) => {
  const [tokenError, setTokenError] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('impersonated_token') || localStorage.getItem('token');
    console.log('[DEBUG] Token letto in CandidatoDashboard:', token);
    if (!token || token === 'null' || token === null) {
      toast.error('Token di accesso non trovato. Ricarica la pagina o torna al pannello admin.');
      window.location.href = '/#/admin';
      setTokenError(true);
      return;
    }
  }, []);
  const [candidato, setCandidato] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRichiestaModal, setShowRichiestaModal] = useState(false);
  const [selectedAgenzia, setSelectedAgenzia] = useState(null);
  const [showScopriDiPiuModal, setShowScopriDiPiuModal] = useState(false);
  const [showAreaProfilo, setShowAreaProfilo] = useState(false);
  const [impersonationSession, setImpersonationSession] = useState(null);
  const [activeBottomTab, setActiveBottomTab] = useState('home');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCVPreview, setShowCVPreview] = useState(false);
  const [showCVModal, setShowCVModal] = useState(false);
  const [selectedMatchForCV, setSelectedMatchForCV] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [existingColloqui, setExistingColloqui] = useState([]);
  const [cvInviati, setCvInviati] = useState([]);

  // Bottom Navigation Items (Max 5 per mobile UX ottimale)
  const navItems = [
    { key: 'home', icon: Home, label: 'Dashboard', emoji: '🏠' },
    { key: 'opportunita', icon: Briefcase, label: 'Opportunità', emoji: '🎯' },
    { key: 'colloqui', icon: MessageCircle, label: 'Colloqui', emoji: '💼' },
    { key: 'feedback-inviati', icon: Star, label: 'I Miei Feedback', emoji: '⭐' },
    { key: 'feed', icon: Camera, label: 'Feed', emoji: '📸' },
    { key: 'profilo', icon: UserCircle, label: 'Profilo', emoji: '👤' }
  ];

  // Menu Hamburger Items (voci secondarie)
  const menuItems = [
    { key: 'notifiche', icon: Bell, label: 'Notifiche', emoji: '🔔' },
    { key: 'formazione', icon: BookOpen, label: 'Formazione', emoji: '🎓' },
    { key: 'impostazioni', icon: Settings, label: 'Impostazioni', emoji: '⚙️' },
    { key: 'assistenza', icon: HelpCircle, label: 'Assistenza', emoji: '🆘' },
    { key: 'logout', icon: LogOut, label: 'Logout', emoji: '🚪' }
  ];

  // No feed preview data - load real data from API

  // No more mock data - show empty state when no real matches

  useEffect(() => {
    if (candidatoId) {
      loadCandidatoData();
      loadMatches();
      loadExistingColloquiAndCV();
      
      // Check for impersonation session
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session');
      if (sessionId) {
        loadImpersonationSession(sessionId);
      }
    }
  }, [candidatoId]);

  const loadExistingColloquiAndCV = async () => {
    try {
      const token = localStorage.getItem('impersonated_token') || localStorage.getItem('token');
      console.log('[DEBUG] Token usato per fetch colloqui:', token);
      const response = await fetch(`${API}/api/v1/candidato/colloqui`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Colloqui caricati:', data);
        // Agenzie con colloqui - converti in numeri per confronto
        const agenzieConColloqui = Array.isArray(data) 
          ? data.map(c => Number(c.agenzia?.id)).filter(Boolean) 
          : [];
        console.log('Agenzie con colloqui (IDs):', agenzieConColloqui);
        setExistingColloqui(agenzieConColloqui);
        // Agenzie con CV inviato - converti in numeri per confronto
        const agenzieConCV = Array.isArray(data) 
          ? data.filter(c => c.cv_candidato_id).map(c => Number(c.agenzia?.id)).filter(Boolean)
          : [];
        console.log('Agenzie con CV inviato (IDs):', agenzieConCV);
        setCvInviati(agenzieConCV);
      }
    } catch (error) {
      console.error('Errore caricamento colloqui/CV:', error);
    }
  };

  const loadCandidatoData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('impersonated_token') || localStorage.getItem('token');
      console.log('[DEBUG] Token usato per fetch candidato:', token);
      const response = await axios.get(`${API}/api/v1/candidato/${candidatoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setCandidato(response.data);
    } catch (error) {
      console.error("Error loading candidato:", error);
      toast.error("Errore nel caricamento dati candidato");
      // Set a fallback candidato object to prevent infinite loading
      setCandidato({
        nome: "Candidato",
        cognome: "Demo", 
        email: `demo-${candidatoId}@example.com`,
        telefono: "N/A",
        citta: "Milano",
        eta: 25
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMatches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('impersonated_token') || localStorage.getItem('token');
      console.log('[DEBUG] Token usato per fetch matches:', token);
      const response = await axios.get(`${API}/api/v1/matches?candidate_id=${candidatoId}`, {
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });
      setMatches(response.data || []);
    } catch (error) {
      console.error("Error loading matches:", error);
      toast.error("Errore nel caricamento match");
    } finally {
      setLoading(false);
    }
  };

  const loadImpersonationSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/admin/impersonation/${sessionId}`);
      if (response.data) {
        setImpersonationSession(response.data);
      }
    } catch (error) {
      console.error("Error loading impersonation session:", error);
      // Se la sessione non è valida, continuiamo normalmente
    }
  };

  const handleReturnToAdmin = () => {
    if (impersonationSession) {
      // Termina la sessione di impersonation
      axios.delete(`${API}/admin/impersonation/${impersonationSession.session_id}`)
        .catch(error => console.error("Error ending impersonation:", error));
    }
    // Reindirizza al pannello admin
    window.location.href = '/#/admin-test'; // Use test route until routing is fixed
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const getMatchColor = (percentage) => {
    if (percentage >= 80) return "bg-blue-500";
    if (percentage >= 60) return "bg-green-500";
    if (percentage >= 40) return "bg-yellow-500";
    return "bg-red-500";
  };

  const handleRichiestaColloquio = (agenzia) => {
    // Se è un match object, trasforma in formato agenzia
    if (agenzia.agency_name) {
      const agenziaFormattata = {
        agenzia_id: agenzia.agenzia_id || extractAgencyIdFromMatch(agenzia),
        agenzia_nome: agenzia.agency_name,
        citta: agenzia.agency_city,
        telefono: agenzia.agency_phone
      };
      setSelectedAgenzia(agenziaFormattata);
    } else {
      setSelectedAgenzia(agenzia);
    }
    setShowRichiestaModal(true);
  };

  // Funzione helper per estrarre l'agenzia_id dal match geografico
  const extractAgencyIdFromMatch = (match) => {
    // Per i match geografici, l'id potrebbe essere nel formato "geo_candidato_agenzia"
    if (match.id && typeof match.id === 'string' && match.id.startsWith('geo_')) {
      const parts = match.id.split('_');
      if (parts.length >= 3) {
        return parseInt(parts[2]); // Estrai l'agenzia_id dalla stringa
      }
    }
    return match.id; // Fallback all'id originale
  };

  const closeRichiestaModal = () => {
    setShowRichiestaModal(false);
    setSelectedAgenzia(null);
  };

  const handleScopriDiPiu = (agenzia) => {
    setSelectedAgenzia(agenzia);
    setShowScopriDiPiuModal(true);
  };

  const closeScopriDiPiuModal = () => {
    setShowScopriDiPiuModal(false);
    setSelectedAgenzia(null);
  };

  // Funzione per inviare CV direttamente dalla preview
  const handleInviaCVFromPreview = async (match) => {
    // Apre il modal per scegliere tra CV generato o carica file
    setSelectedMatchForCV(match);
    setShowCVModal(true);
  };

  // Gestione file CV
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validazione dimensione (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Il file è troppo grande. Dimensione massima: 5MB');
        return;
      }
      // Validazione tipo file
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Formato file non supportato. Usa PDF, DOC o DOCX');
        return;
      }
      setCvFile(file);
    }
  };

  // Invio CV (generato o caricato)
  const handleSendCV = async () => {
    const token = localStorage.getItem('impersonated_token') || localStorage.getItem('token');
    console.log('[DEBUG] Token usato per fetch generica:', token);
    if (!token) {
      toast.error('Errore di autenticazione');
      return;
    }

    const agenziaId = selectedMatchForCV.agenzia_id || extractAgencyIdFromMatch(selectedMatchForCV);
    const agenziaName = selectedMatchForCV.agency_name || selectedMatchForCV.agenzia_nome || 'Agenzia';

    if (!agenziaId) {
      toast.error('Errore: ID agenzia non trovato');
      return;
    }

    try {
      toast.loading('Invio CV in corso...', { id: 'cv-sending' });
      
      let response;
      if (cvFile) {
        // Invio CV caricato
        const formData = new FormData();
        formData.append('file', cvFile);
        
        response = await fetch(
          `${API}/api/v1/cv/invia-cv-file/${agenziaId}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          }
        );
      } else {
        // Invio CV generato dal profilo
        response = await fetch(
          `${API}/api/v1/cv/invia-cv/${agenziaId}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      if (response.ok) {
        const data = await response.json();
        toast.success(`CV inviato con successo a ${agenziaName}!`, { id: 'cv-sending' });
        setShowCVModal(false);
        setSelectedMatchForCV(null);
        setCvFile(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || `Errore nell'invio del CV a ${agenziaName}`, { id: 'cv-sending' });
      }
    } catch (error) {
      console.error('Errore invio CV:', error);
      toast.error(`Errore nell'invio del CV a ${agenziaName}`, { id: 'cv-sending' });
    }
  };

  const calculateProfileCompletion = (candidato) => {
    if (!candidato) return 0;
    
    const fields = [
      candidato.nome,
      candidato.cognome,
      candidato.email,
      candidato.telefono,
      candidato.citta,
      candidato.zona_preferenza_lavorativa,
      candidato.tipo_impiego_preferito,
      candidato.mobilita_trasporto
    ];
    
    const filledFields = fields.filter(field => field && field.trim() !== '').length;
    const totalFields = fields.length;
    
    return Math.round((filledFields / totalFields) * 100);
  };

  // Render content based on active navigation
  const renderContent = () => {
    switch (activeBottomTab) {
      case 'home':
        return renderDashboardHome();
      case 'opportunita':
        return <AgenzieCompatibili 
          candidatoId={candidatoId} 
          onRichiestaColloquio={handleRichiestaColloquio}
          onScopriDiPiu={handleScopriDiPiu}
        />;
      case 'colloqui':
        return <ColloquiList userType="candidato" />;
      case 'feedback-inviati':
        return <FeedbackRiepilogoCandidato candidateId={candidatoId} />;
      case 'formazione':
        return <FormazioneView visibilita="candidati" titolo="Formazione & Crescita" />;
      case 'feed':
        return <FeedAgenzie />;
      case 'messaggi':
        return renderMessaggi();
      case 'notifiche':
        return renderNotifiche();
      case 'impostazioni':
        return renderImpostazioni();
      case 'profilo':
        return renderProfilo();
      case 'assistenza':
        return renderAssistenza();
      default:
        return renderDashboardHome();
    }
  };

  const renderDashboardHome = () => (
    <div className="space-y-6">
      {/* Profile Completion Card */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="w-5 h-5 text-blue-500" />
            Completamento Profilo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Progresso</span>
              <span className="text-sm font-medium">{calculateProfileCompletion(candidato)}%</span>
            </div>
            <Progress value={calculateProfileCompletion(candidato)} className="h-2" />
            <p className="text-xs text-gray-500">
              Completa il tuo profilo per aumentare le possibilità di match con le agenzie
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Feed Agenzie Preview */}
      <Card className="border-l-4 border-l-purple-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">📰</span>
              Feed Agenzie
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveBottomTab('feed')}
              className="text-xs px-3 py-1 h-7 bg-gradient-to-r from-purple-500 to-blue-600 text-white border-0 hover:shadow-lg"
            >
              Vai al Feed completo
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-100">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-sm text-gray-900">Immobiliare Milano Centro</h4>
                    <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-200">Milano</Badge>
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2">
                    🏢 Siamo alla ricerca di 3 nuovi agenti per il nostro team! Se hai passione per l'immobiliare...
                  </p>
                  <div className="flex items-center gap-4 mt-2">
                    <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors">
                      <Heart className="w-3 h-3" />
                      <span>12</span>
                    </button>
                    <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-500 transition-colors">
                      <MessageCircle className="w-3 h-3" />
                      <span>3</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Scopri le ultime opportunità pubblicate dalle agenzie partner</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agency Matches */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-500" />
              Agenzie Compatibili ({matches.length})
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveBottomTab('opportunita')}
              className="text-xs px-3 py-1 h-7 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
            >
              Vedi tutte
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent> 
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="animate-pulse bg-gray-200 h-32 rounded-lg"></div>
              ))}
            </div>
          ) : matches.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
              {matches.slice(0, 6).map((match, index) => {
                const agenziaName = match.agency_name || 'Agenzia non specificata';
                const agenziaId = Number(match.agency_id || match.id) || index;
                const affinita = match.affinita_geografica || 0;
                const haColloquio = existingColloqui.includes(agenziaId);
                const haCVInviato = cvInviati.includes(agenziaId);
                
                console.log(`Agenzia ${agenziaName} (ID: ${agenziaId}) - Colloquio: ${haColloquio}, CV: ${haCVInviato}`);
                
                return (
                  <div key={agenziaId} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <h3 className="font-semibold text-sm text-gray-900 truncate flex-1">
                          {agenziaName}
                        </h3>
                        <Badge className={`ml-2 ${getMatchColor(affinita)} text-white text-xs px-2 py-1`}>
                          {affinita}%
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{match.agency_city || 'Città non specificata'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          <span>{match.match_type === 'esplicito' ? 'Match attivo' : 'Match geografico'}</span>
                        </div>
                      </div>

                      <div className="flex gap-1.5">
                        <div className="relative group flex-1">
                          <Button
                            size="sm"
                            onClick={() => !haColloquio && handleRichiestaColloquio(match)}
                            disabled={haColloquio}
                            className={`w-full text-xs h-8 ${
                              haColloquio 
                                ? 'bg-gray-300 cursor-not-allowed opacity-60' 
                                : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
                            }`}
                            title={haColloquio ? 'Colloquio già richiesto' : 'Richiedi colloquio'}
                          >
                            {haColloquio ? '✓ Richiesto' : '📞 Colloquio'}
                          </Button>
                          {haColloquio && (
                            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                              Hai già richiesto un colloquio
                            </div>
                          )}
                        </div>
                        <WhatsAppButton
                          telefono={match.agency_phone || 'Telefono non disponibile'}
                          nome={agenziaName}
                          tipo="agenzia"
                          className="flex-1 text-xs h-8"
                        />
                        <div className="relative group">
                          <Button
                            size="sm"
                            onClick={() => !haCVInviato && handleInviaCVFromPreview(match)}
                            disabled={haCVInviato}
                            className={`px-2.5 text-xs h-8 ${
                              haCVInviato 
                                ? 'bg-gray-300 cursor-not-allowed opacity-60' 
                                : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                            }`}
                            title={haCVInviato ? 'CV già inviato' : 'Invia CV'}
                          >
                            {haCVInviato ? '✓' : <FileText className="w-3 h-3" />}
                          </Button>
                          {haCVInviato && (
                            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                              CV già inviato
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleScopriDiPiu(match)}
                          className="px-2.5 text-xs h-8"
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nessuna agenzia compatibile al momento</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Feed Agenzie Preview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <span className="text-lg">📰</span>
              Feed Agenzie - Ultime Novità
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveBottomTab('feed')}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300"
            >
              <span>Vai al Feed completo</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nessun post disponibile al momento</p>
            <p className="text-sm mt-1">Le agenzie inizieranno a condividere contenuti presto!</p>
          </div>
          <div className="mt-4 text-center">
            <Button
              onClick={() => setActiveBottomTab('feed')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
            >
              ➡️ Vai al Feed completo
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderMessaggi = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-500" />
          I tuoi Messaggi
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Nessun messaggio al momento</p>
          <p className="text-sm mt-1">I tuoi messaggi con le agenzie appariranno qui</p>
        </div>
      </CardContent>
    </Card>
  );

  const renderNotifiche = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-blue-600" />
          Notifiche
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center py-12">
          <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Nessuna notifica al momento</p>
          <p className="text-sm text-gray-400 mt-2">Ti aggiorneremo quando ci saranno novità</p>
        </div>
      </CardContent>
    </Card>
  );

  const renderAssistenza = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-blue-600" />
          Assistenza
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">💬 Contatta il Team InLeads</h3>
            <p className="text-sm text-blue-700 mb-3">
              Hai bisogno di aiuto? Siamo qui per te!
            </p>
            <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600">
              <MessageSquare className="w-4 h-4 mr-2" />
              Invia Messaggio
            </Button>
          </div>
          <div className="text-center text-sm text-gray-500">
            <p>📧 Email: info@inleads.it</p>
            <p className="mt-1">Tempo di risposta medio: 24 ore</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderImpostazioni = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-gray-500" />
          Impostazioni
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="font-medium">Notifiche</span>
            <Button variant="outline" size="sm">Gestisci</Button>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="font-medium">Privacy</span>
            <Button variant="outline" size="sm">Modifica</Button>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="font-medium">Preferenze</span>
            <Button variant="outline" size="sm">Configura</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderProfilo = () => (
    <div>
      {showAreaProfilo && (
        <AreaProfiloCandidato
          candidato={candidato}
          onClose={() => setShowAreaProfilo(false)}
          onSave={(updatedCandidato) => {
            setCandidato(updatedCandidato);
            setShowAreaProfilo(false);
          }}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle className="w-5 h-5 text-purple-500" />
            Il Mio Profilo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mx-auto mb-3 flex items-center justify-center text-white text-2xl font-bold">
                {candidato?.nome?.charAt(0)}{candidato?.cognome?.charAt(0)}
              </div>
              <h3 className="text-lg font-semibold">{candidato?.nome} {candidato?.cognome}</h3>
              <p className="text-gray-600">{candidato?.zona_preferenza_lavorativa}</p>
            </div>
            <Button
              onClick={() => setShowAreaProfilo(true)}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white mb-2"
            >
              Modifica Profilo
            </Button>
            <Button
              onClick={() => setShowCVPreview(true)}
              variant="outline"
              className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <FileText className="w-4 h-4 mr-2" />
              Visualizza CV
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const getMatchBadge = (level) => {
    const badges = {
      alta: { color: "bg-blue-100 text-blue-800", icon: "🔵", text: "Alta compatibilità" },
      media: { color: "bg-green-100 text-green-800", icon: "🟢", text: "Media compatibilità" },
      bassa: { color: "bg-yellow-100 text-yellow-800", icon: "🟡", text: "Bassa compatibilità" },
      scarsa: { color: "bg-red-100 text-red-800", icon: "🔴", text: "Scarsa compatibilità" }
    };
    return badges[level] || badges.scarsa;
  };

  useEffect(() => {
    if (tokenError) return;
    if (candidatoId) {
      loadCandidatoData();
      loadMatches();
      // Check for impersonation session
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session');
      if (sessionId) {
        loadImpersonationSession(sessionId);
      }
    }
  }, [candidatoId, tokenError]);

  if (tokenError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center p-8">
        <User className="w-16 h-16 text-red-400 mb-4" />
        <h2 className="text-2xl font-bold text-red-600 mb-2">Accesso non autorizzato</h2>
        <p className="text-gray-600 mb-4">Token di accesso non trovato.<br />Ricarica la pagina o torna al pannello admin.</p>
        <Button onClick={() => window.location.href = '/#/admin-test'} className="bg-red-500 text-white rounded-xl px-6 py-2 mt-2">Torna all'Admin</Button>
      </div>
    );
  }
  // Se è aperta l'area profilo, mostra quella
  if (showAreaProfilo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
        <AreaProfiloCandidato 
          candidatoId={candidatoId}
          onClose={() => setShowAreaProfilo(false)}
        />
      </div>
    );
  }

  // Removed duplicate render functions - using the ones that are called by renderContent at line 201

  // Removed duplicate renderContent function - using the one at line 201

  const renderOriginalDashboard = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Caricamento profilo...</p>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Header Candidato */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-3xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold">
                    {candidato.nome} {candidato.cognome}
                  </CardTitle>
                  <p className="text-blue-100 flex items-center gap-2 mt-1">
                    <MapPin className="w-4 h-4" />
                    {candidato.citta || "Città non specificata"}
                    {candidato.eta && <span>• {candidato.eta} anni</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setShowAreaProfilo(true)}
                  variant="ghost"
                  className="text-white hover:bg-white/20 rounded-xl border border-white/20"
                >
                  <User className="w-4 h-4 mr-2" />
                  Il Mio Profilo
                </Button>
                <div className="text-right space-y-2">
                  <Badge className="bg-white/20 text-white border-white/30 block">
                    {candidato.esperienza_immobiliare ? "Con esperienza" : "Senza esperienza"}
                  </Badge>
                  {(() => {
                    const completion = calculateProfileCompletion(candidato);
                    const isComplete = completion >= 90;
                    return (
                      <Badge className={`${isComplete ? 'bg-green-500/20 text-green-100 border-green-400/30' : 'bg-yellow-500/20 text-yellow-100 border-yellow-400/30'} block`}>
                        {isComplete ? `✅ Profilo ${completion}%` : `📝 Completa profilo ${completion}%`}
                      </Badge>
                    );
                  })()}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Tipo impiego preferito</p>
                  <p className="font-semibold">{candidato.tipo_impiego_preferito || "Non specificato"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Zona preferita</p>
                  <p className="font-semibold">{candidato.zona_preferenza_lavorativa || "Non specificata"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Mobilità</p>
                  <p className="font-semibold">{candidato.mobilita_trasporto || "Non specificata"}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Agenzie Compatibili */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              Agenzie Compatibili con Te
            </CardTitle>
            <p className="text-gray-600">
              Abbiamo trovato {matches.length} agenzie che potrebbero essere interessate al tuo profilo
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Calcolando compatibilità...</p>
              </div>
            ) : matches.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Nessuna agenzia trovata. Completa il tuo profilo per migliorare i match!</p>
              </div>
            ) : (
              matches.map((match, index) => {
                const badge = getMatchBadge(match.match_level);
                return (
                  <div key={match.agenzia_id} className="bg-gray-50 rounded-2xl p-4 hover:bg-gray-100 transition-all duration-200 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">{match.agenzia_nome}</h3>
                          <p className="text-sm text-gray-600 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {match.agenzia_zona}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">{match.match_percentage}%</div>
                        <Badge className={`${badge.color} border-0 text-xs px-2 py-1`}>
                          {badge.icon} {badge.text}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Progress Bar Animata */}
                    <div className="mb-4">
                      <Progress 
                        value={match.match_percentage} 
                        className={`h-2 ${getMatchColor(match.match_percentage)}`}
                      />
                    </div>
                    
                    {/* Dettagli Match */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                      <div className="text-center">
                        <div className="text-gray-600">Zona</div>
                        <div className="font-semibold">{match.details.zona_score}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-600">Esperienza</div>
                        <div className="font-semibold">{match.details.esperienza_score}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-600">Impiego</div>
                        <div className="font-semibold">{match.details.impiego_score}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-600">Mobilità</div>
                        <div className="font-semibold">{match.details.mobilita_score}%</div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => handleScopriDiPiu(match)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Scopri di più
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1 rounded-xl border-blue-300 text-blue-600 hover:bg-blue-50"
                        onClick={() => handleRichiestaColloquio(match)}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Richiedi Colloquio
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Statistiche Personali */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <CardContent className="p-6 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <div className="text-2xl font-bold">{matches.length}</div>
              <div className="text-blue-100 text-sm">Agenzie interessate</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
            <CardContent className="p-6 text-center">
              <Star className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <div className="text-2xl font-bold">
                {matches.length > 0 ? Math.round(matches.reduce((acc, m) => acc + m.match_percentage, 0) / matches.length) : 0}%
              </div>
              <div className="text-green-100 text-sm">Match medio</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-6 text-center">
              <Target className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <div className="text-2xl font-bold">
                {matches.filter(m => m.match_percentage >= 80).length}
              </div>
              <div className="text-purple-100 text-sm">Match elevati</div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header with Logo and Desktop Navigation */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo and Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setActiveBottomTab('home')}
                className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-xl flex items-center justify-center shadow-sm border border-gray-100 flex-shrink-0 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
                title="Torna alla Dashboard"
              >
                <img 
                  src={agentAdvancerMain} 
                  alt="InLeads" 
                  className="w-6 h-6 sm:w-8 sm:h-8"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="text-blue-600 font-bold text-xs">IL</span>';
                  }}
                />
              </button>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                  {candidato ? `${candidato.nome} ${candidato.cognome}` : 'Dashboard Candidato'}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Area Personale</p>
              </div>
            </div>

            {/* Desktop Horizontal Navigation */}
            <nav className="hidden md:flex items-center space-x-1 bg-gray-50 rounded-2xl p-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = activeBottomTab === item.key;
                
                return (
                  <button
                    key={item.key}
                    onClick={() => setActiveBottomTab(item.key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 font-medium text-sm ${
                      active 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-base">{item.emoji}</span>
                    <span className="hidden lg:block">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Profile Menu - Desktop & Mobile */}
            <div className="flex items-center gap-3 relative user-menu-container">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold hover:shadow-lg transition-all duration-200"
              >
                {candidato?.nome?.charAt(0)}{candidato?.cognome?.charAt(0)}
              </button>
              
              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">
                      {candidato?.nome} {candidato?.cognome}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{candidato?.email}</p>
                  </div>

                  {/* Menu Items */}
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    const isLogout = item.key === 'logout';
                    
                    return (
                      <button
                        key={item.key}
                        onClick={() => {
                          setShowUserMenu(false);
                          if (isLogout) {
                            if (impersonationSession) {
                              window.location.href = '/admin';
                            } else {
                              window.location.href = '/login';
                            }
                          } else {
                            setActiveBottomTab(item.key);
                          }
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-200 ${
                          isLogout 
                            ? 'text-red-600 hover:bg-red-50 border-t border-gray-100 mt-2 pt-3' 
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${isLogout ? 'text-red-600' : 'text-gray-500'}`} />
                        <span className="text-sm font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto flex">
        {/* Desktop Sidebar Navigation - Alternative Layout */}
        <aside className="hidden xl:block w-64 p-4">
          <nav className="space-y-2 sticky top-24">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = activeBottomTab === item.key;
              
              return (
                <button
                  key={item.key}
                  onClick={() => setActiveBottomTab(item.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-medium text-left ${
                    active 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-lg">{item.emoji}</span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 p-4 pb-20 md:pb-4">
          {/* Banner Impersonation */}
          {impersonationSession && (
            <ImpersonationBanner 
              userName={impersonationSession.impersonated_user_name}
              userType="candidato"
              onReturnToAdmin={handleReturnToAdmin}
              className="mb-6"
            />
          )}
          
          {/* Render content based on active tab */}
          {renderContent()}
        </main>
      </div>

      {/* Modal Richiesta Colloquio */}
      <RichiestaColloquioModal
        isOpen={showRichiestaModal}
        onClose={closeRichiestaModal}
        candidatoId={candidatoId}
        agenziaId={selectedAgenzia?.agenzia_id}
        agenziaNome={selectedAgenzia?.agenzia_nome}
      />

      {/* Modal Scopri di Più */}
      <ScopriDiPiuModal
        isOpen={showScopriDiPiuModal}
        onClose={closeScopriDiPiuModal}
        agenzia={selectedAgenzia}
      />

      {/* Modal Anteprima CV */}
      <CVPreviewModal
        candidatoData={candidato}
        isOpen={showCVPreview}
        onClose={() => setShowCVPreview(false)}
      />

      {/* Bottom Navigation - Mobile Only (Max 5 Items) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 md:hidden shadow-lg">
        <div 
          className="flex items-center justify-around px-2 py-2" 
          style={{ 
            height: '68px',
            paddingBottom: 'env(safe-area-inset-bottom, 8px)',
            maxWidth: '100vw'
          }}
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = activeBottomTab === item.key;
            
            return (
              <button
                key={item.key}
                onClick={() => setActiveBottomTab(item.key)}
                className={`flex flex-col items-center justify-center gap-1 transition-all duration-200 flex-1 rounded-xl py-2 ${
                  active 
                    ? 'text-blue-600 transform scale-105 bg-blue-50' 
                    : 'text-gray-500 hover:text-gray-700 active:scale-95'
                }`}
                style={{ 
                  minHeight: '56px',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation'
                }}
              >
                <Icon 
                  className={`transition-all duration-200 ${
                    active ? 'w-6 h-6' : 'w-5 h-5'
                  }`} 
                  strokeWidth={active ? 2.5 : 2}
                />
                <span className={`text-xs font-medium transition-all duration-200 ${
                  active ? 'text-blue-600' : 'text-gray-500'
                }`} style={{ fontSize: '10px' }}>
                  {item.label === 'Feed Agenzie' ? 'Feed' : item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Modal CV - Scegli tra generato o carica file */}
      {showCVModal && selectedMatchForCV && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Invia CV
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedMatchForCV.agency_name || selectedMatchForCV.agenzia_nome || 'Agenzia'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCVModal(false);
                    setSelectedMatchForCV(null);
                    setCvFile(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {/* Opzione 1: CV Generato */}
              <div className="border-2 border-blue-200 rounded-xl p-6 bg-blue-50">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      CV generato dal profilo
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Invia un CV automaticamente generato dai tuoi dati del profilo
                    </p>
                    <button
                      onClick={handleSendCV}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Invia CV Generato
                    </button>
                  </div>
                </div>
              </div>

              {/* Divisore */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">oppure</span>
                </div>
              </div>

              {/* Opzione 2: Carica File */}
              <div className="border-2 border-emerald-200 rounded-xl p-6 bg-emerald-50">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center">
                    <Upload className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Carica il tuo CV
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Carica un file PDF, DOC o DOCX (max 5MB)
                    </p>
                    
                    {/* File Input */}
                    <div className="space-y-3">
                      <label className="block">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          className="hidden"
                          id="cv-file-input"
                        />
                        <div className="border-2 border-dashed border-emerald-300 rounded-lg p-4 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-100 transition-colors">
                          <Upload className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">
                            {cvFile ? cvFile.name : 'Clicca per selezionare un file'}
                          </p>
                          {cvFile && (
                            <p className="text-xs text-gray-500 mt-1">
                              {(cvFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          )}
                        </div>
                      </label>

                      {/* Bottoni azione */}
                      {cvFile && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCvFile(null)}
                            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
                          >
                            Rimuovi
                          </button>
                          <button
                            onClick={handleSendCV}
                            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            <Send className="w-4 h-4" />
                            Invia File
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidatoDashboard;
